import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Theme, useTheme } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { ReactNode } from "react";

import MDBox from "@/components/atoms/MDBox";
import { useMaterialUIController } from "@/contexts/mui-context";

import { item, itemArrow, itemContent } from "./styles/sidenavItem";

interface SidenavItemProps {
  name: string;
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  active?: boolean | string;
  nested?: boolean;
  children?: ReactNode;
  open?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

function SidenavItem({
  name,
  color = "dark",
  active = false,
  nested = false,
  children = false,
  open = false,
  disabled = false,
  icon,
  onClick,
  ...rest
}: SidenavItemProps) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const theme = useTheme();
  const hasIcon = !!icon;
  const itemContentStyles = itemContent(theme, {
    active,
    miniSidenav,
    name,
    open,
    nested,
    hasIcon,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  });

  return (
    <>
      <ListItem
        onClick={disabled ? undefined : onClick}
        {...rest}
        component="li"
        sx={(theme: Theme) => {
          const isLightSidenav = (transparentSidenav && !darkMode) || whiteSidenav;
          const disabledColor = isLightSidenav ? "rgba(52, 71, 103, 0.55)" : "rgba(255, 255, 255, 0.55)";

          return {
            ...item(theme, {
              active,
              color,
              nested,
              disabled,
              transparentSidenav,
              whiteSidenav,
              darkMode,
            }),
            ...(disabled
              ? {
                  "& .MuiListItemText-primary, & .MuiTypography-root": {
                    color: `${disabledColor} !important`,
                  },
                  "& .MuiBox-root::before": {
                    backgroundColor: disabledColor,
                    color: disabledColor,
                    opacity: "1 !important",
                  },
                  "& .sidenav-item-icon": {
                    color: `${disabledColor} !important`,
                    opacity: "1 !important",
                  },
                }
              : {}),
          };
        }}
      >
        <MDBox sx={itemContentStyles}>
          {hasIcon && (
            <MDBox
              className="sidenav-item-icon"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: "absolute",
                left: nested ? "-1rem" : "-1.125rem",
                top: "50%",
                transform: "translateY(-50%)",
                color: "inherit",
                lineHeight: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: nested ? "0.95rem" : "1.05rem",
                },
              }}
            >
              {icon}
            </MDBox>
          )}
          <ListItemText primary={name} />
          {children && (
            <ExpandLessIcon
              sx={(theme) =>
                itemArrow(theme, {
                  open,
                  miniSidenav,
                  transparentSidenav,
                  whiteSidenav,
                  darkMode,
                })
              }
            />
          )}
        </MDBox>
      </ListItem>
      {children && (
        <Collapse in={open} timeout={{ enter: 250, exit: 200 }} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

export default SidenavItem;
